import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Calendar, MapPin, FileText, Image, Crown, Zap, Plane, Globe, Lock, Users, Settings, Copy, CalendarClock } from 'lucide-react';
import { createEvent } from '@/services/eventService';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { EventType, EventVisibilityType, EVENT_TYPES } from '@/types/event';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from '@/integrations/supabase/client';

interface CreateEventDialogProps {
  onEventCreated: () => void;
}

const CreateEventDialog: React.FC<CreateEventDialogProps> = ({ onEventCreated }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    location: '',
    image_url: '',
    capacity: '',
    is_premium: false,
    event_type: 'standard' as EventType,
    duration_days: '',
    program_details: '',
    destination_country: '',
    min_participants: '',
    max_participants: '',
    target_audience: '',
    visibility_type: 'internal' as EventVisibilityType,
    public_registration_enabled: false,
    external_registration_limit: '',
    registration_form_fields: {
      name: true,
      email: true,
      phone: false,
      company: false
    }
  });

  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .single();
      setIsAdmin(!!data);
    };
    checkAdminRole();
  }, [user]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Connexion requise", {
        description: "Veuillez vous connecter pour créer un événement."
      });
      return;
    }

    if (!formData.title || !formData.start_date) {
      toast.error("Champs requis", {
        description: "Le titre et la date de début sont obligatoires."
      });
      return;
    }

    setIsLoading(true);

    try {
      const eventData = {
        title: formData.title,
        description: formData.description || null,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        location: formData.location || null,
        image_url: formData.image_url || null,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        is_premium: formData.is_premium,
        created_by: user.id,
        event_type: formData.event_type,
        duration_days: formData.duration_days ? parseInt(formData.duration_days) : null,
        program_details: formData.program_details || null,
        destination_country: formData.destination_country || null,
        min_participants: formData.min_participants ? parseInt(formData.min_participants) : null,
        max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
        target_audience: formData.target_audience || null,
        visibility_type: formData.visibility_type,
        public_slug: null,
        public_registration_enabled: formData.public_registration_enabled,
        external_registration_limit: formData.external_registration_limit ? parseInt(formData.external_registration_limit) : null,
        registration_form_fields: formData.registration_form_fields,
        landing_page_content: {},
        seo_metadata: {}
      };

      const result = await createEvent(eventData);
      
      if (result) {
        const publicUrl = result.public_slug 
          ? `${window.location.origin}/event/public/${result.public_slug}`
          : null;
        
        toast.success("Événement créé", {
          description: publicUrl ? (
            <div className="space-y-2">
              <p>Votre événement a été créé avec succès.</p>
              <div className="flex items-center gap-2 p-2 bg-primary/10 rounded mt-2">
                <Globe className="h-4 w-4" />
                <a 
                  href={publicUrl} 
                  target="_blank" 
                  className="text-xs text-primary underline flex-1 truncate"
                >
                  {publicUrl}
                </a>
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="h-6 px-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(publicUrl);
                    toast.success("Lien copié !");
                  }}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ) : "Votre événement a été créé avec succès.",
          duration: 8000
        });
        
        // Reset form
        setFormData({
          title: '',
          description: '',
          start_date: '',
          end_date: '',
          location: '',
          image_url: '',
          capacity: '',
          is_premium: false,
          event_type: 'standard' as EventType,
          duration_days: '',
          program_details: '',
          destination_country: '',
          min_participants: '',
          max_participants: '',
          target_audience: '',
          visibility_type: 'internal' as EventVisibilityType,
          public_registration_enabled: false,
          external_registration_limit: '',
          registration_form_fields: {
            name: true,
            email: true,
            phone: false,
            company: false
          }
        });
        
        setOpen(false);
        onEventCreated();
      } else {
        toast.error("Erreur", {
          description: "Impossible de créer l'événement."
        });
      }
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error("Erreur", {
        description: "Une erreur est survenue lors de la création."
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="primary">
          <Plus className="mr-2 h-4 w-4" />
          Ajouter un événement
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Créer un nouvel événement
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Type d'événement */}
          <div className="space-y-2">
            <Label htmlFor="event_type">Type d'événement *</Label>
            <Select value={formData.event_type} onValueChange={(value: EventType) => handleInputChange('event_type', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionnez le type d'événement" />
              </SelectTrigger>
              <SelectContent>
                {Object.values(EVENT_TYPES)
                  .filter(type => !(type as any).adminOnly || isAdmin)
                  .map((type) => {
                    const IconComponent = 
                      type.icon === 'Zap' ? Zap : 
                      type.icon === 'Plane' ? Plane : 
                      type.icon === 'MapPin' ? MapPin :
                      type.icon === 'CalendarClock' ? CalendarClock :
                      Calendar;
                    return (
                      <SelectItem key={type.id} value={type.id}>
                        <div className="flex items-center gap-2">
                          <IconComponent className="h-4 w-4" style={{ color: type.color }} />
                          <span>{type.label}</span>
                          <span className="text-xs text-muted-foreground">- {type.description}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
          </div>

          {/* Type de visibilité */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              Type de visibilité *
            </Label>
            
            <RadioGroup 
              value={formData.visibility_type} 
              onValueChange={(value: EventVisibilityType) => 
                handleInputChange('visibility_type', value)
              }
            >
              <div className="flex items-center space-x-2 p-3 border rounded hover:bg-accent cursor-pointer">
                <RadioGroupItem value="internal" id="internal" />
                <Label htmlFor="internal" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">Interne</div>
                      <div className="text-xs text-muted-foreground">
                        Réservé aux membres NODE uniquement
                      </div>
                    </div>
                  </div>
                </Label>
              </div>

              <div className="flex items-center space-x-2 p-3 border rounded hover:bg-accent cursor-pointer">
                <RadioGroupItem value="external" id="external" />
                <Label htmlFor="external" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-primary" />
                    <div>
                      <div className="font-medium">Externe (Public)</div>
                      <div className="text-xs text-muted-foreground">
                        Page publique avec inscriptions ouvertes
                      </div>
                    </div>
                  </div>
                </Label>
              </div>

              <div className="flex items-center space-x-2 p-3 border rounded hover:bg-accent cursor-pointer">
                <RadioGroupItem value="hybrid" id="hybrid" />
                <Label htmlFor="hybrid" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-purple-500" />
                    <div>
                      <div className="font-medium">Hybride</div>
                      <div className="text-xs text-muted-foreground">
                        Membres + invités externes limités
                      </div>
                    </div>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Options pour événements externes/hybrides */}
          {(formData.visibility_type === 'external' || formData.visibility_type === 'hybrid') && (
            <div className="space-y-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
              <h4 className="font-medium flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Configuration de l'inscription publique
              </h4>

              {/* Toggle inscriptions publiques */}
              <div className="flex items-center space-x-2">
                <Switch
                  id="public_registration_enabled"
                  checked={formData.public_registration_enabled}
                  onCheckedChange={(checked) => 
                    handleInputChange('public_registration_enabled', checked)
                  }
                />
                <Label htmlFor="public_registration_enabled">
                  Activer les inscriptions publiques
                </Label>
              </div>

              {/* Limite d'inscrits externes */}
              <div className="space-y-2">
                <Label htmlFor="external_registration_limit">
                  Limite d'inscrits externes (optionnel)
                </Label>
                <Input
                  id="external_registration_limit"
                  type="number"
                  value={formData.external_registration_limit}
                  onChange={(e) => 
                    handleInputChange('external_registration_limit', e.target.value)
                  }
                  placeholder="Laisser vide pour illimité"
                  min="1"
                />
              </div>

              {/* Champs du formulaire d'inscription */}
              <div className="space-y-2">
                <Label>Champs du formulaire d'inscription</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="field_name" 
                      checked={formData.registration_form_fields.name}
                      onCheckedChange={(checked) => 
                        handleInputChange('registration_form_fields', {
                          ...formData.registration_form_fields,
                          name: !!checked
                        })
                      }
                    />
                    <Label htmlFor="field_name" className="text-sm">Nom complet</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="field_email" 
                      checked={true}
                      disabled
                    />
                    <Label htmlFor="field_email" className="text-sm opacity-50">
                      Email (obligatoire)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="field_phone" 
                      checked={formData.registration_form_fields.phone}
                      onCheckedChange={(checked) => 
                        handleInputChange('registration_form_fields', {
                          ...formData.registration_form_fields,
                          phone: !!checked
                        })
                      }
                    />
                    <Label htmlFor="field_phone" className="text-sm">Téléphone</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="field_company" 
                      checked={formData.registration_form_fields.company}
                      onCheckedChange={(checked) => 
                        handleInputChange('registration_form_fields', {
                          ...formData.registration_form_fields,
                          company: !!checked
                        })
                      }
                    />
                    <Label htmlFor="field_company" className="text-sm">Entreprise</Label>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Titre *
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Nom de l'événement"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Lieu
              </Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="Adresse ou lieu"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Décrivez votre événement..."
              rows={3}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_date">Date de début *</Label>
              <Input
                id="start_date"
                type="datetime-local"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="end_date">Date de fin</Label>
              <Input
                id="end_date"
                type="datetime-local"
                value={formData.end_date}
                onChange={(e) => handleInputChange('end_date', e.target.value)}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="image_url" className="flex items-center gap-2">
                <Image className="h-4 w-4" />
                URL de l'image
              </Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => handleInputChange('image_url', e.target.value)}
                placeholder="https://..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="capacity">Capacité</Label>
              <Input
                id="capacity"
                type="number"
                value={formData.capacity}
                onChange={(e) => handleInputChange('capacity', e.target.value)}
                placeholder="Nombre max de participants"
                min="1"
              />
            </div>
          </div>
          
          {/* Champs spécifiques selon le type */}
          {(formData.event_type === 'bootcamp' || formData.event_type === 'lex') && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duration_days">Durée (jours)</Label>
                <Input
                  id="duration_days"
                  type="number"
                  value={formData.duration_days}
                  onChange={(e) => handleInputChange('duration_days', e.target.value)}
                  placeholder="Nombre de jours"
                  min="1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="target_audience">Public cible</Label>
                <Input
                  id="target_audience"
                  value={formData.target_audience}
                  onChange={(e) => handleInputChange('target_audience', e.target.value)}
                  placeholder="Entrepreneurs, développeurs..."
                />
              </div>
            </div>
          )}

          {formData.event_type === 'lex' && (
            <div className="space-y-2">
              <Label htmlFor="destination_country">Pays de destination</Label>
              <Input
                id="destination_country"
                value={formData.destination_country}
                onChange={(e) => handleInputChange('destination_country', e.target.value)}
                placeholder="Pays visité"
              />
            </div>
          )}

          {formData.event_type === 'soft_landing' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="destination_country">Pays d'intégration</Label>
                <Input
                  id="destination_country"
                  value={formData.destination_country}
                  onChange={(e) => handleInputChange('destination_country', e.target.value)}
                  placeholder="Pays d'accompagnement"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="duration_days">Durée d'accompagnement (jours)</Label>
                <Input
                  id="duration_days"
                  type="number"
                  value={formData.duration_days}
                  onChange={(e) => handleInputChange('duration_days', e.target.value)}
                  placeholder="Durée en jours"
                  min="1"
                />
              </div>
            </div>
          )}

          {(formData.event_type === 'bootcamp' || formData.event_type === 'lex') && (
            <div className="space-y-2">
              <Label htmlFor="program_details">Détails du programme</Label>
              <Textarea
                id="program_details"
                value={formData.program_details}
                onChange={(e) => handleInputChange('program_details', e.target.value)}
                placeholder="Programme détaillé, agenda, activités..."
                rows={3}
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="min_participants">Participants minimum</Label>
              <Input
                id="min_participants"
                type="number"
                value={formData.min_participants}
                onChange={(e) => handleInputChange('min_participants', e.target.value)}
                placeholder="Nombre minimum"
                min="1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max_participants">Participants maximum</Label>
              <Input
                id="max_participants"
                type="number"
                value={formData.max_participants}
                onChange={(e) => handleInputChange('max_participants', e.target.value)}
                placeholder="Nombre maximum"
                min="1"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_premium"
              checked={formData.is_premium}
              onCheckedChange={(checked) => handleInputChange('is_premium', checked)}
            />
            <Label htmlFor="is_premium" className="flex items-center gap-2">
              <Crown className="h-4 w-4 text-amber-500" />
              Événement premium
            </Label>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setOpen(false)}
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              variant="primary"
            >
              {isLoading ? "Création..." : "Créer l'événement"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateEventDialog;